import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import EntryDetail from "../components/EntryDetail";
import DeleteEntryModal from "../components/DeleteEntryModal";
import { useSettings } from "../context/SettingsContext";
import client from "../api/client";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const STATE_ORDER = ['Before', 'During', 'After', 'Integration'];

const STATE_COLORS = {
  Before:      { bg: 'rgba(255,255,255,0.07)',  border: 'rgba(255,255,255,0.15)',   text: 'rgba(255,255,255,0.6)'  },
  During:      { bg: 'rgba(255,211,80,0.1)',     border: 'rgba(255,211,80,0.3)',     text: '#ffd350'                },
  After:       { bg: 'rgba(125,211,132,0.1)',    border: 'rgba(125,211,132,0.3)',    text: '#7dd384'                },
  Integration: { bg: 'rgba(180,125,232,0.1)',    border: 'rgba(180,125,232,0.3)',    text: '#b47de8'                },
};

function SessionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [entries, setEntries] = useState([]);
  const [deleteModal, setDeleteModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [activeState, setActiveState] = useState(null); // null = show all
  const sessionRef = useRef(null);

  useEffect(() => {
    client.get(`/sessions/${id}`)
      .then(res => {
        setEntries(res.data);
        // Default to showing all entries
        setActiveState(null);
      })
      .catch(() => setError("Unable to load session."))
      .finally(() => setLoading(false));
  }, [id]);

  /*
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try {
      await client.delete(`/entries/${entryId}`);
      const remaining = entries.filter(e => e.id !== entryId);
      if (remaining.length === 0) {
        navigate("/");
      } else {
        setEntries(remaining);
      }
    } catch {
      alert("Failed to delete entry.");
    }
  };*/

  const handleDeleteEntry = async (entryId) => {
  try {
    const res = await client.get(`/entries/${entryId}/session-info`);
    setDeleteModal({ entryId, isRoot: res.data.isRoot, childCount: res.data.childCount });
  } catch {
    alert("Failed to load entry info.");
  }
};

const confirmDelete = async (deleteAll = false) => {
  const { entryId } = deleteModal;
  setDeleteModal(null);
  try {
    await client.delete(`/entries/${entryId}${deleteAll ? "?deleteAll=true" : ""}`);
    if (deleteAll) {
      navigate("/");
    } else {
      const remaining = entries.filter(e => e.id !== entryId);
      if (remaining.length === 0) navigate("/");
      else setEntries(remaining);
    }
  } catch {
    alert("Failed to delete entry.");
  }
};


  const handleExportPDF = async () => {
    if (!sessionRef.current) return;
    setExporting(true);
    try {
      const el = sessionRef.current;

      const header = document.createElement("div");
      header.id = "pdf-header";
      header.innerHTML = `<div style="font-family:'Comfortaa',cursive;font-size:22px;font-weight:400;color:#000000;letter-spacing:1px;margin-bottom:20px;padding-bottom:12px;border-bottom:1px solid #dddddd;text-align:right;">Journey Trails</div>`;
      el.insertBefore(header, el.firstChild);

      const style = document.createElement("style");
      style.id = "pdf-export-style";
      style.innerHTML = `
        [data-pdf-capture] * { color: #1a1a1a !important; background: #ffffff !important; border-color: #dddddd !important; }
        [data-pdf-capture] h1, [data-pdf-capture] h2, [data-pdf-capture] h3 { color: #000000 !important; }
      `;
      document.head.appendChild(style);
      el.setAttribute("data-pdf-capture", "true");

      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff", scale: 2, useCORS: true, logging: false,
      });

      el.removeAttribute("data-pdf-capture");
      document.getElementById("pdf-export-style")?.remove();
      document.getElementById("pdf-header")?.remove();

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const usableWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * usableWidth) / canvas.width;
      const usableHeight = pageHeight - margin * 2;

      let yOffset = 0;
      while (yOffset < imgHeight) {
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, margin - (yOffset * pageWidth) / usableWidth, usableWidth, imgHeight);
        yOffset += usableHeight;
      }

      const root = entries.find(e => !e.session_id) || entries[0];
      const filename = root?.subcategory
        ? `${root.subcategory.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_session.pdf`
        : `journey_session_${id}.pdf`;
      pdf.save(filename);
    } catch (err) {
      console.error("PDF export failed:", err);
      sessionRef.current?.removeAttribute("data-pdf-capture");
      document.getElementById("pdf-export-style")?.remove();
      document.getElementById("pdf-header")?.remove();
      alert("Failed to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <p>Loading session…</p>;
  if (error)   return <p>{error}</p>;
  if (!entries.length) return <p>Session not found.</p>;

  const root = entries.find(e => !e.session_id) || entries[0];
  const isMushroom = root.medicine_type === "mushroom";
  const loggedStates = new Set(entries.map(e => e.state));
  const dateStr = new Date(root.session_date)
    .toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' });

  const visibleEntries = activeState
    ? entries.filter(e => e.state === activeState)
    : entries;

  return (
    <main className="EntryDetailPage">

      {/* TOP BAR */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <span
          onClick={() => navigate("/")}
          style={{ cursor: 'pointer', fontSize: '14px', opacity: 0.7 }}
        >
          
        </span>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <span
            onClick={handleExportPDF}
            style={{ cursor: exporting ? 'default' : 'pointer', fontSize: '14px', opacity: exporting ? 0.5 : 0.7 }}
          >
            {exporting ? 'Exporting…' : '↓ PDF'}
          </span>
        </div>
      </div>

      {/* SESSION HEADER */}
      <div style={{
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${isMushroom ? 'rgba(180,125,232,0.2)' : 'rgba(125,211,232,0.2)'}`,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <span style={{ fontSize: '20px' }}>{isMushroom ? '🌀' : '🌿'}</span>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '500' }}>
            {root.subcategory || 'Untitled Session'}
          </h1>
          <span style={{ fontSize: '13px', opacity: 0.5, marginLeft: 'auto' }}>{dateStr}</span>
        </div>

        {/* STATE PILLS — clickable to filter */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {STATE_ORDER.map(s => {
            const logged = loggedStates.has(s);
            const active = activeState === s;
            const c = STATE_COLORS[s];
            return (
              <span
                key={s}
                onClick={() => logged && setActiveState(active ? null : s)}
                style={{
                  fontSize: '12px',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  background: logged ? (active ? c.bg : 'transparent') : 'transparent',
                  border: `1px solid ${logged ? c.border : 'rgba(255,255,255,0.08)'}`,
                  color: logged ? c.text : 'rgba(255,255,255,0.2)',
                  cursor: logged ? 'pointer' : 'default',
                  fontStyle: logged ? 'normal' : 'italic',
                  transition: 'background 0.15s',
                }}
              >
                {s}
                {logged && (
                  <span style={{ marginLeft: '5px', fontSize: '10px', opacity: 0.5 }}>
                    {active ? '✕' : ''}
                  </span>
                )}
              </span>
            );
          })}
          {activeState && (
            <span
              onClick={() => setActiveState(null)}
              style={{ fontSize: '11px', opacity: 0.4, cursor: 'pointer', alignSelf: 'center', marginLeft: '4px' }}
            >
              show all
            </span>
          )}
        </div>
      </div>

      {/* ENTRIES */}
      <div ref={sessionRef}>
        {visibleEntries.map((entry, index) => {
          const c = STATE_COLORS[entry.state] || STATE_COLORS.Before;
          return (
            <div key={entry.id}>
              {/* State label + entry actions */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '8px', marginTop: index > 0 ? '32px' : '0',
              }}>
                <span style={{
                  fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase',
                  color: c.text, opacity: 0.85,
                }}>
                  ✦ {entry.state || 'Entry'}
                </span>
                <div style={{ display: 'flex', gap: '14px' }} data-html2canvas-ignore="true">
                  <span
                    onClick={() => navigate(`/entry/${entry.id}/edit`)}
                    style={{ cursor: 'pointer', fontSize: '13px', opacity: 0.6 }}
                  >
                    ✎ Edit
                  </span>
                  <span
                    onClick={() => handleDeleteEntry(entry.id)}
                    style={{ cursor: 'pointer', fontSize: '13px', color: '#ffaa33' }}
                  >
                    🗑 Delete
                  </span>
                </div>
              </div>

              <EntryDetail entry={entry} />

              {index < visibleEntries.length - 1 && (
                <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', margin: '8px 0' }} />
              )}
            </div>
          );
        })}
      </div>

      {/* ADD ANOTHER ENTRY TO THIS SESSION */}
      <div style={{ marginTop: '32px', textAlign: 'center' }}>
        <span
          onClick={() => navigate(`/new?session=${root.id}&medicine=${root.medicine_type}&subcategory=${encodeURIComponent(root.subcategory || '')}&method=${encodeURIComponent(root.method || '')}`)}
          style={{
            fontSize: '13px', opacity: 0.85, cursor: 'pointer',
            border: `1px solid ${isMushroom ? 'rgba(180,125,232,0.25)' : 'rgba(125,211,232,0.25)'}`,
            borderRadius: '20px', padding: '8px 20px',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
          onMouseLeave={e => e.currentTarget.style.opacity = '0.85'}
        >
          + Add entry to this session
        </span>
      </div>
      {deleteModal && (
        <DeleteEntryModal
          isRoot={deleteModal.isRoot}
          childCount={deleteModal.childCount}
          onDeleteAll={() => confirmDelete(true)}
          onDeleteRoot={() => confirmDelete(false)}
          onDeleteSingle={() => confirmDelete(false)}
          onCancel={() => setDeleteModal(null)}
        />
      )}
    </main>
  );
}

export default SessionDetailPage;